import type { Rec, CompKey } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fnum, fpct } from "../lib/format.js";
import { COMP_LABEL, COMP_ICON, CATS } from "../lib/comps.js";
import { Donut } from "../components/Charts.js";
import { ComponentTable } from "../components/ComponentTable.js";

const EXTRAS: CompKey[] = ["entrance_fee", "transport", "guide", "train"];

function LegLine({ label, v, total, color }: {
  label: string; v: number; total: number; color: string;
}) {
  return (
    <div className="li">
      <span className="sw" style={{ background: color }} />
      <span>{label}</span>
      <span className="n">{fnum(v)}</span>
      <span className="p">{fpct(v, total, 0)}</span>
    </div>
  );
}

export function Components({ rows, allRows }: { rows: Rec[]; allRows: Rec[] }) {
  const any = EXTRAS.some((c) => rows.some((r) => r.comp === c));

  /* Components whose status columns are completely empty in the Masterfile. */
  const zero = EXTRAS.filter((c) => {
    const cr = allRows.filter((r) => r.comp === c);
    return cr.length > 0 && !cr.some((r) => r.received || r.nrDone || r.estDone || r.ccDate);
  });

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 5</span>
          <h2><i className="bi bi-boxes hico" aria-hidden="true" />Additional rate components</h2>
          <span className="note">Entrance Fee &middot; Transportation &middot; Guide Fee &middot; Train</span>
        </div>
        <div className="grid g-2">
          {EXTRAS.map((c) => {
            const cr = rows.filter((r) => r.comp === c);
            const s = computeStats(cr);
            const noCat = cr.filter((r) => !r.cat).length;
            const present = CATS.filter((c2) => cr.some((r) => r.cat === c2));
            return (
              <div className="card" key={c}>
                <div className="card-head">
                  <h3><i className={`bi ${COMP_ICON[c]} hico`} aria-hidden="true" />{COMP_LABEL[c]}</h3>
                  <span className="sub">{fnum(s.total)} record{s.total === 1 ? "" : "s"}</span>
                </div>
                <div style={{
                  display: "flex", gap: 6, alignItems: "center",
                  flexWrap: "wrap", padding: "6px 10px",
                }}>
                  <div style={{ flex: "none" }}>
                    <Donut
                      size={124} thick={14}
                      centerValue={s.total ? `${s.completion.toFixed(0)}%` : "–"}
                      centerLabel="complete" aria={`${COMP_LABEL[c]} completion`}
                      segments={[
                        { label: "Priced", value: s.xPriced, color: "var(--s-done)" },
                        { label: "Estimated only", value: s.xEstOnly, color: "var(--s-est)" },
                        { label: "Awaiting pricing", value: s.xAwaiting, color: "var(--s-await)" },
                        { label: "Outstanding", value: s.xNone, color: "var(--s-none)" },
                      ]}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div className="legend">
                      <LegLine label="CR received" v={s.received} total={s.total} color="var(--s-done)" />
                      <LegLine label="Priced (Done)" v={s.priced} total={s.total} color="var(--s-done)" />
                      <LegLine label="Estimated (Done)" v={s.estimated} total={s.total} color="var(--s-est)" />
                      <LegLine label="Not received" v={s.notReceived} total={s.total} color="var(--s-none)" />
                      <LegLine label="Outstanding" v={s.estOutstanding} total={s.total} color="var(--s-none)" />
                      <LegLine label="Crosschecked" v={s.crosschecked} total={s.total} color="var(--s-done)" />
                      <LegLine label="Exported" v={s.exported} total={s.total} color="var(--s-done)" />
                    </div>
                  </div>
                </div>
                {noCat > 0 ? (
                  <div className="tblnote">
                    {fnum(noCat)} of {fnum(cr.length)} rows carry no Category value in the
                    Masterfile, so an A&ndash;E breakdown is not available for this component.
                  </div>
                ) : cr.length > 0 ? (
                  <div className="tblnote">
                    Categories present:{" "}
                    {present.map((c2) => (
                      <span key={c2}>
                        <span className="catbadge">{c2}</span>{" "}
                        {cr.filter((r) => r.cat === c2).length}
                        &nbsp;&nbsp;
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {!any && (
        <div className="card">
          <div className="empty">No component records match the current filters.</div>
        </div>
      )}

      <ComponentTable
        rows={rows.filter((r) => EXTRAS.includes(r.comp))}
        title="Additional components — detail"
      />

      {zero.length > 0 && (
        <div className="sec">
          <div className="callout">
            <span className="ic" />
            <div>
              <h3><i className="bi bi-info-circle hico" aria-hidden="true" />No progress recorded yet for {zero.map((c) => COMP_LABEL[c]).join(", ")}</h3>
              <p>
                All status columns in {zero.length === 1 ? "this sheet are" : "these sheets are"}{" "}
                empty in the Masterfile &mdash; no CR receive status, no input status, and no
                crosscheck, upload or export dates. The zeros here are what the file actually
                says, not a calculation error.
              </p>
              <p>
                Note that the workbook&rsquo;s own Overview tab shows Guide Fee as{" "}
                <code>28 / 100%</code> estimated. That comes from a{" "}
                <code>COUNTIF(&lt;&gt;&quot;Received New Rate&quot;)</code> formula, which counts
                the 28 <i>blank</i> rows as matches. This dashboard counts only an explicit{" "}
                <code>Done</code>, so it reports 0.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
